import type { Account, DraftAccount } from '@interfaces/account';
import { GoalStates } from '@interfaces/goalEnums';
import type { Goal } from '@interfaces/goalSubAccountTypes';
import type { User } from '@interfaces/user';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { legacyColors } from '@ovComponents/legacyColor';
import { getGoalTitle } from '@ovComponents/resources';
import { Typography } from 'hephaestus/web';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Select only active goals related to the an individual (not houeholds)
export const getValidGoals = (userData: User) =>
  (userData?.goals ?? []).filter(
    (goal: Goal) =>
      goal.state === GoalStates.ACTIVE && !goal?.householdClientGroup?.id,
  );

type GoalLinkingInputProps = {
  goals: Goal[];
  goalsLinked: Goal[];
  helpText?: string;
  error?: boolean;
  errorText?: string;
  success?: boolean;
  successText?: string;
  setGoalsLinked: (goalsLinked: Goal[]) => void;
  onBlur?: (e: React.ChangeEvent) => void;
};

export const GoalLinkingInput = (props: GoalLinkingInputProps) => {
  const { goals, goalsLinked, setGoalsLinked, ...restProps } = props;
  const { t } = useTranslation(['client', 'workflowCompletions']);
  const { workflowData } = useContext(WorkflowContext);

  useEffect(() => {
    if (workflowData.currentGoalId) {
      setGoalsLinked([
        {
          id: workflowData.currentGoalId,
          name: workflowData.currentGoalName,
          user: {} as User,
        },
      ]);
    }
  });

  return (
    <>
      <Typography
        variant='headingXSmall'
        sx={{ mb: 1, color: legacyColors.neutral800 }}
      >
        {t('workflowCompletions:goalLinking.title')}
      </Typography>
      <Typography
        variant='bodyMedium'
        sx={{ mb: 1, color: legacyColors.neutral600, fontWeight: 500 }}
      >
        {t('workflowCompletions:goalLinking.info')}
      </Typography>
      <Autocomplete
        label={t('workflowCompletions:goalLinking.inputLabel')}
        options={goals}
        value={goalsLinked ?? []}
        multiple
        fullWidth
        getOptionLabel={(option: any) => getGoalTitle(option)}
        onChange={(_e: React.SyntheticEvent, value: any) =>
          setGoalsLinked(value)
        }
        {...restProps}
      />
    </>
  );
};

export const GoalLinkingReview = ({
  account,
}: {
  account: Account & DraftAccount;
}) => {
  const { t } = useTranslation(['client', 'workflowCompletions']);
  if (!account?.goalsLinked?.length) return <></>;
  const goalLabels = account.goalsLinked.map((goal) => getGoalTitle(goal));
  const goalLabelsWithBreaks = goalLabels
    .flatMap((el) => [<br />, el])
    .slice(1);
  return (
    <>
      <Typography variant='labelLarge' sx={{ fontWeight: 600 }}>
        {t('workflowCompletions:goalLinking:goalsLinked')}:
      </Typography>
      <Typography
        variant='bodyMedium'
        sx={{ textAlign: 'right', fontWeight: 500 }}
      >
        {goalLabelsWithBreaks}
      </Typography>
    </>
  );
};
